import { StrictMode, Component } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import App from './App.jsx';
import { AppProvider } from './context.jsx';

// Sentry — только если задан DSN. Иначе no-op.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
  });
}

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(err, info) {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(err, { contexts: { react: { componentStack: info?.componentStack } } });
    }
  }
  render() {
    if (this.state.error) return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: '#030303', color: '#ede8e0', fontFamily: "'Syne', sans-serif" }}>
        <div style={{ color: '#C4944A', fontSize: 14, letterSpacing: 4, textTransform: 'uppercase' }}>Something went wrong</div>
        <button onClick={() => location.reload()} style={{ padding: '16px 40px', background: '#C4944A', color: '#030303', border: 'none', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer' }}>Reload</button>
      </div>
    );
    return this.props.children;
  }
}

// View Transitions API — плавный morphing thumbnail→ArtworkDetail.
// Перехватываем клики по внутренним <a href="/...">, оборачиваем navigate
// в document.startViewTransition. Браузер сам интерполирует viewTransitionName.
// Работает в Chrome 111+ / Edge / Opera. Safari/Firefox — fallback на обычный переход.
if (typeof document !== 'undefined' && document.startViewTransition) {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const link = e.target.closest('a[href^="/"]');
    if (!link) return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;
    const href = link.getAttribute('href');
    // skip если ссылка ведёт на текущую страницу или это hash-ссылка
    if (!href || href.startsWith('#') || href === location.pathname + location.search) return;
    e.preventDefault();
    document.startViewTransition(() => {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  });
}

// Yandex.Metrika (если задан ID)
const ymId = parseInt(import.meta.env.VITE_YM_ID);
if (ymId) {
  const s = document.createElement('script');
  s.async = true; s.src = 'https://mc.yandex.ru/metrika/tag.js';
  s.onload = () => { window.ym?.(ymId, 'init', { clickmap: true, trackLinks: true, accurateTrackBounce: true, webvisor: true }); };
  document.head.appendChild(s);
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AppProvider>
            <App />
          </AppProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </StrictMode>
);
